/** @jsxImportSource @emotion/react */
import {
  Box, Color, elevation, Overflow, ZIndex,
} from '@gousto-internal/citrus-react'
import { useMemo } from 'react'
import { SearchResult as SearchResultType } from '../../types/entities'
import { isMobile } from '../../utils/isMobile'
import { NoSearchResult } from '../NoSearchResult'
import { SearchFAQ } from '../SearchFAQ'
import { SearchResultArticles } from '../SearchResultArticles'

type SearchResultProps = {
  isVisible: boolean,
  searchResult: SearchResultType | null,
  onShowMore?: () => void,
  error?: unknown,
  query: string,
}

const SearchResult = ({
  isVisible,
  onShowMore,
  error,
  searchResult,
  query,
}: SearchResultProps) => {
  const result = useMemo(() => {
    if (error) {
      return <NoSearchResult query={query} />
    }
    if (searchResult?.results) {
      if (searchResult.results.length > 0) {
        return (
          <SearchResultArticles
            searchResult={searchResult}
            onShowMore={onShowMore}
            query={query || ''}
          />
        )
      } else {
        return <NoSearchResult query={query} />
      }
    } else {
      return (query.length < 3 && onShowMore && isVisible) && (
        <SearchFAQ
          withArticle
          isMobileOnly
          query={query}
          onShowMore={onShowMore}
        />
      )
    }
  }, [searchResult, onShowMore, query, error, isVisible])

  return (
    <Box
      css={{
        transition: 'max-height 0.175s ease',
        position: 'absolute',
        marginTop: isMobile() ? '16px' : 0,
      }}
      width="100%"
      bg={Color.NeutralGrey_50}
      boxShadow={elevation[3]}
      zIndex={ZIndex.Modal}
      overflowY={Overflow.Hidden}
      maxHeight={isVisible ? '500px' : '0'}
      data-testing="searchResult"
      onMouseDown={(e) => e.preventDefault()}
    >
      {result}
    </Box>
  )
}

export { SearchResult }
export type { SearchResultProps }
